import { type ReactElement } from 'react';
import { Twitter, Linkedin, Facebook, Instagram, Youtube, Mail } from 'lucide-react';
import { Logo } from './Logo';

interface FooterLink {
  label: string;
  href: string;
}

interface FooterColumn {
  title: string;
  links: FooterLink[];
}

const footerColumns: FooterColumn[] = [
  {
    title: 'Produkt',
    links: [
      { label: 'Funktionen', href: '/funktionen' },
      { label: 'Preise', href: '/preise' },
      { label: 'Integrationen', href: '/funktionen/integrationen' },
      { label: 'API', href: '/api-schnittstelle' },
      { label: 'Updates', href: '/updates' },
    ],
  },
  {
    title: 'Unternehmen',
    links: [
      { label: 'Über uns', href: '/ueber-uns' },
      { label: 'Karriere', href: '/karriere' },
      { label: 'Presse', href: '/presse' },
      { label: 'Partner', href: '/partner' },
      { label: 'Blog', href: '/blog' },
    ],
  },
  {
    title: 'Rechtliches',
    links: [
      { label: 'Datenschutz', href: '/datenschutz' },
      { label: 'Impressum', href: '/impressum' },
      { label: 'AGB', href: '/agb' },
      { label: 'Cookie-Einstellungen', href: '#cookie-settings' },
      { label: 'Lizenzen', href: '/lizenzen' },
    ],
  },
  {
    title: 'Support',
    links: [
      { label: 'Hilfe-Center', href: '/hilfe' },
      { label: 'Kontakt', href: '/kontakt' },
      { label: 'Status', href: '/status' },
      { label: 'Dokumentation', href: '/dokumentation' },
      { label: 'Community', href: '/community' },
    ],
  },
];

const socialLinks = [
  { icon: Twitter, href: '#twitter', label: 'Twitter' },
  { icon: Linkedin, href: '#linkedin', label: 'LinkedIn' },
  { icon: Facebook, href: '#facebook', label: 'Facebook' },
  { icon: Instagram, href: '#instagram', label: 'Instagram' },
  { icon: Youtube, href: '#youtube', label: 'YouTube' },
];

export default function Footer(): ReactElement {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-secondary-900 text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-6 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            {/* Logo */}
            <a href="/" className="mb-6 inline-flex items-center" aria-label="Saldify">
              <Logo light className="h-9 w-auto" />
            </a>

            {/* Tagline */}
            <p className="text-secondary-400 mb-6 max-w-xs">
              Die moderne Cloud-Lösung für Ihre Buchhaltung. Einfach, sicher und
              DSGVO-konform.
            </p>

            {/* Newsletter */}
            <div className="mb-6">
              <p className="text-sm font-medium mb-3">Newsletter abonnieren</p>
              <form className="flex gap-2">
                <div className="relative flex-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-500" />
                  <input
                    type="email"
                    placeholder="Ihre E-Mail"
                    className="w-full bg-secondary-800 border border-secondary-700 rounded-lg pl-10 pr-4 py-2.5 text-sm text-white placeholder:text-secondary-500 focus:outline-none focus:border-primary-500 transition-colors"
                  />
                </div>
                <button
                  type="submit"
                  className="bg-primary-600 hover:bg-primary-700 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors"
                >
                  Senden
                </button>
              </form>
            </div>

            {/* Social Links */}
            <div className="flex gap-4">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  className="w-10 h-10 bg-secondary-800 hover:bg-secondary-700 rounded-lg flex items-center justify-center transition-colors"
                  aria-label={social.label}
                >
                  <social.icon className="w-5 h-5 text-secondary-400" />
                </a>
              ))}
            </div>
          </div>

          {/* Link Columns */}
          {footerColumns.map((column) => (
            <div key={column.title}>
              <h3 className="font-semibold text-white mb-4">{column.title}</h3>
              <ul className="space-y-3">
                {column.links.map((link) =>
                  link.href === '#cookie-settings' ? (
                    <li key={link.label}>
                      <button
                        type="button"
                        onClick={() =>
                          window.dispatchEvent(new Event('saldify:open-cookie-settings'))
                        }
                        className="text-secondary-400 hover:text-white transition-colors text-sm"
                      >
                        {link.label}
                      </button>
                    </li>
                  ) : (
                    <li key={link.label}>
                      <a
                        href={link.href}
                        className="text-secondary-400 hover:text-white transition-colors text-sm"
                      >
                        {link.label}
                      </a>
                    </li>
                  )
                )}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-secondary-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            {/* Copyright */}
            <p className="text-secondary-500 text-sm">
              &copy; {currentYear} CuiveMedia. Alle Rechte vorbehalten.
            </p>

            {/* Certifications/Badges */}
            <div className="flex items-center gap-6">
              <span className="text-secondary-500 text-xs flex items-center gap-2">
                <span className="w-2 h-2 bg-success-500 rounded-full" />
                SSL-verschlüsselt
              </span>
              <span className="text-secondary-500 text-xs flex items-center gap-2">
                <span className="w-2 h-2 bg-success-500 rounded-full" />
                DSGVO-konform
              </span>
              <span className="text-secondary-500 text-xs flex items-center gap-2">
                <span className="w-2 h-2 bg-success-500 rounded-full" />
                Made in Germany
              </span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
